import React from 'react';
import { motion } from 'framer-motion';
import { getCourseContent } from '../../utils/progressUtils';

const easing = [0.22, 1, 0.36, 1];

/**
 * ContentModalHeader — "logbook" band.
 * One soft tinted band. Course name set as a typographic stamp.
 * The bottom edge carries a hash-mark trail (tape-measure) that mirrors the footer trail.
 */
const ContentModalHeader = ({
  showAchievementView,
  isPathCompletedLocal,
  modalNavigation,
  userCurrentCourse,
  studentPath,
  pathCourses,
  getCourseProgressLocal,
  getCompletedContentForCourseLocal,
  courseProgress,
  onClose
}) => {
  const isPathView = showAchievementView || isPathCompletedLocal();

  const getTotalContentWithoutReinforcements = (course) => {
    if (!course) return 0;
    const courseContent = getCourseContent(course);
    return courseContent.filter(content => {
      const isReinforcement = course.reinforcements?.some(r => r._id === content._id);
      return !isReinforcement;
    }).length;
  };

  const totalContentCount = modalNavigation.isViewingBackwards && modalNavigation.viewingCourse
    ? getTotalContentWithoutReinforcements(modalNavigation.viewingCourse)
    : getTotalContentWithoutReinforcements(userCurrentCourse);

  const displayCourseTitle = isPathView
    ? studentPath?.title
    : modalNavigation.isViewingBackwards && modalNavigation.viewingCourse
      ? modalNavigation.viewingCourse.title
      : userCurrentCourse?.title;

  const displayProgress = modalNavigation.isViewingBackwards && modalNavigation.viewingCourse
    ? getCourseProgressLocal(modalNavigation.viewingCourse._id)
    : courseProgress;

  const completedCount = getCompletedContentForCourseLocal(
    modalNavigation.isViewingBackwards && modalNavigation.viewingCourse
      ? modalNavigation.viewingCourse._id
      : userCurrentCourse?._id
  );

  const progressPercent = Math.max(0, Math.min(100, isPathView ? 100 : displayProgress || 0));
  const total = isPathView ? pathCourses.length : totalContentCount;
  const done = isPathView ? pathCourses.length : completedCount;

  return (
    <>
      {/* Close — restrained, no halo */}
      <motion.button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 w-8 h-8 rounded-full text-foreground/60 hover:text-foreground hover:bg-foreground/5 transition-colors flex items-center justify-center"
        aria-label="Close"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay: 0.3, ease: easing }}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </motion.button>

      {/* Logbook band — slides down from above */}
      <motion.div
        className="relative flex-shrink-0 bg-secondary"
        style={{ borderBottom: '1px solid hsl(var(--border))' }}
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.55, ease: easing }}
      >
        <div className="max-w-5xl mx-auto px-5 sm:px-10 pt-4 pb-3">
          {/* Eyebrow stamp */}
          <motion.div
            className="flex items-center gap-2.5 mb-2"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.2, ease: easing }}
          >
            <span
              className="inline-block h-px w-6 bg-primary"
              aria-hidden="true"
            />
            <span className="text-[10px] font-bold text-primary tracking-[0.22em] uppercase">
              {isPathView ? 'Learning Path' : 'Course'}
            </span>
          </motion.div>

          {/* Course title as a typographic statement */}
          <motion.h3
            className="text-xl sm:text-[26px] font-bold text-foreground font-display tracking-tight leading-[1.15] pr-10"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.28, ease: easing }}
          >
            {displayCourseTitle}
          </motion.h3>

          {/* Progress line — same treatment as dashboard */}
          <motion.div
            className="mt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.45, ease: easing }}
          >
            <div className="relative h-[3px] bg-foreground/10 rounded-full overflow-visible">
              <motion.div
                className="absolute inset-y-0 left-0 bg-primary rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.9, delay: 0.6, ease: easing }}
              >
                {progressPercent > 0 && progressPercent < 100 && (
                  <span
                    className="absolute right-0 top-1/2 w-2.5 h-2.5 bg-primary rounded-full"
                    style={{ transform: 'translate(50%, -50%)' }}
                    aria-hidden="true"
                  />
                )}
              </motion.div>
            </div>
            <div className="mt-2 flex items-baseline justify-between text-[11px] font-bold uppercase tracking-[0.18em] tabular-nums">
              <span className="text-primary">{Math.round(progressPercent)}<span className="text-primary/60">%</span></span>
              <span className="text-foreground/55">
                {isPathView
                  ? <>{pathCourses.length} <span className="text-foreground/25">·</span> {pathCourses.length} <span className="text-foreground/40">courses</span></>
                  : <>{String(done).padStart(2, '0')} <span className="text-foreground/25">·</span> {String(total).padStart(2, '0')} <span className="text-foreground/40">items</span></>
                }
              </span>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
};

export default ContentModalHeader;
